import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Card,
  Typography,
  Drawer,
  Tag,
  Space,
  Statistic,
  Row,
  Col,
  Button,
  Switch,
  Input,
  Tooltip,
} from "antd";
import {
  FileExcelOutlined,
  HistoryOutlined,
  InboxOutlined,
  ShopOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../configs";
import { getUser } from "../../utils/DataUser";
import dayjs from "dayjs";
import { handleExportExcel } from "../../utils";

const { Title, Text } = Typography;

// Styling for masa timbun status
const getMasaTimbunStatus = (days) => {
  if (days > 3 * 365)
    return { color: "#ff4d4f", label: "Critical", bg: "#fff1f0" };
  if (days > 365) return { color: "#faad14", label: "Warning", bg: "#fffbe6" };
  if (days > 180) return { color: "#1890ff", label: "Normal", bg: "#e6f7ff" };
  return { color: "#52c41a", label: "Fresh", bg: "#f6ffed" };
};

// === KOMPONEN UNTUK MENAMPILKAN HISTORY SATU NOPEN ===
function DocHistory({ doc }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosInstance.get("/monitoring-stock/history", {
          params: {
            id_customer: doc.id_customer,
            item_code: doc.item_code,
            nopen: doc.nopen,
            seri: doc.seri,
          },
        });
        setRows(res.data || []);
      } catch (e) {
        console.error("load history error", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [doc]);

  const columns = [
    {
      title: "Tanggal",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => (
        <Text style={{ fontSize: 12 }}>
          {value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-"}
        </Text>
      ),
      width: 140,
    },
    {
      title: "Jenis Kegiatan",
      dataIndex: "type_label",
      key: "type_label",
      width: 130,
      render: (text) => (
        <Tag
          color={
            text?.includes("Pembongkaran")
              ? "green"
              : text?.includes("Pengeluaran")
                ? "red"
                : "blue"
          }
        >
          {text || "-"}
        </Tag>
      ),
    },
    {
      title: "No. Kegiatan",
      dataIndex: "no_kegiatan",
      key: "no_kegiatan",
      width: 180,
      render: (text) => <Text style={{ fontSize: 12 }}>{text || "-"}</Text>,
    },
    {
      title: "Lot",
      dataIndex: "lokasi_lot",
      key: "lokasi_lot",
      width: 80,
      align: "center",
      render: (text) => <Tag>{text || "-"}</Tag>,
    },
    {
      title: "Jumlah",
      dataIndex: "jumlah_kemasan",
      key: "jumlah_kemasan",
      width: 100,
      align: "right",
      render: (val, record) => {
        const isOutgoing =
          record.type_label === "Outgoing" ||
          record.type_label === "Pemusnahan";
        const displayVal = isOutgoing ? -Math.abs(val) : val;
        return (
          <Text
            strong
            style={{
              color: isOutgoing ? "#ff4d4f" : val > 0 ? "#52c41a" : "#000",
            }}
          >
            {displayVal > 0 ? `+${displayVal}` : displayVal}
          </Text>
        );
      },
    },
    {
      title: "Catatan",
      dataIndex: "catatan",
      key: "catatan",
      ellipsis: true,
      render: (text) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {text || "-"}
        </Text>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={rows}
      loading={loading}
      pagination={{
        pageSize: 10,
        showTotal: (total) => `Total ${total} transaksi`,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      rowKey={(r) => r.id_item_stock_history}
      size="small"
      style={{ background: "#fafafa" }}
    />
  );
}

export default function MonitoringStock() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showZero, setShowZero] = useState(false);
  const [searchText, setSearchText] = useState("");

  const user = getUser();
  const canViewSummary =
    user &&
    user.id_level !== 8 &&
    user.id_user_level !== 8 &&
    user.id_level !== "8" &&
    user.id_user_level !== "8";
  const canViewDetail =
    user &&
    (user.id_level === 8 ||
      user.id_user_level === 8 ||
      user.id_level === "8" ||
      user.id_user_level === "8" ||
      user.id_level === 1 ||
      user.id_user_level === 1 ||
      user.id_level === "1" ||
      user.id_user_level === "1");

  const onNopenClick = (doc) => {
    setSelectedDoc(doc);
    setDrawerVisible(true);
  };

  const loadData = async (includeZero = showZero) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/monitoring-stock", {
        params: {
          show_zero: includeZero ? 1 : 0,
        },
      });
      let fetchedData = res.data || [];
      const user = getUser();
      if (user && user.id_level === 7) {
        fetchedData = fetchedData.filter(
          (d) =>
            d.id_customer === user.username ||
            d.nama_customer === user.username,
        );
      }
      setData(fetchedData);
    } catch (e) {
      console.error("load monitoring stock error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(false);
  }, []);

  const handleToggleShowZero = (checked) => {
    setShowZero(checked);
    loadData(checked);
  };

  // Filter client-side based on showZero and searchText
  const displayData = useMemo(() => {
    const q = (searchText || "").toLowerCase().trim();

    return data
      .map((cust) => {
        // Filter items
        let items = (cust.items || []).filter((it) => {
          const itHasStock =
            Number(it.nilai_kemasan || 0) > 0 ||
            Number(it.nilai_subsatuan || 0) > 0 ||
            Number(it.nilai_satuan || 0) > 0;
          if (!showZero && !itHasStock) return false;
          return true;
        });

        // Filter docs inside item
        items = items.map((it) => {
          const docs = (it.docs || []).filter((d) => {
            const docHasStock =
              Number(d.nilai_kemasan || 0) > 0 ||
              Number(d.nilai_subsatuan || 0) > 0 ||
              Number(d.nilai_satuan || 0) > 0;
            if (!showZero && !docHasStock) return false;
            return true;
          });
          return { ...it, docs };
        });

        // If searching
        if (q) {
          const matchCust =
            (cust.nama_customer || "").toLowerCase().includes(q) ||
            (cust.id_customer || "").toLowerCase().includes(q);

          if (!matchCust) {
            items = items.filter(
              (it) =>
                (it.item_code || "").toLowerCase().includes(q) ||
                (it.item_name || "").toLowerCase().includes(q) ||
                (it.docs || []).some(
                  (d) =>
                    (d.nopen || "").toLowerCase().includes(q) ||
                    (d.lokasi_lot || "").toLowerCase().includes(q)
                )
            );
          }
        }

        const custHasStock =
          Number(cust.nilai_kemasan || 0) > 0 ||
          Number(cust.nilai_subsatuan || 0) > 0 ||
          Number(cust.nilai_satuan || 0) > 0;

        const isVisible = showZero
          ? (q ? items.length > 0 || (cust.nama_customer || "").toLowerCase().includes(q) : true)
          : (items.length > 0 && (custHasStock || items.some((i) => i.docs.length > 0)));

        return {
          ...cust,
          items,
          _visible: isVisible,
        };
      })
      .filter((cust) => cust._visible);
  }, [data, showZero, searchText]);

  // Calculate totals
  const grandTotalCT = useMemo(() => {
    return displayData.reduce(
      (sum, row) => sum + (Number(row.nilai_kemasan ?? row.total_ct) || 0),
      0
    );
  }, [displayData]);

  const grandTotalSatuan = useMemo(() => {
    return Number(
      displayData
        .reduce(
          (sum, row) => sum + (Number(row.nilai_satuan ?? row.total_liter) || 0),
          0
        )
        .toFixed(2)
    );
  }, [displayData]);

  const grandTotalSubsatuan = useMemo(() => {
    return displayData.reduce(
      (sum, row) => sum + (Number(row.nilai_subsatuan ?? row.total_pce) || 0),
      0
    );
  }, [displayData]);

  const totalCustomers = displayData.length;

  // Level 3: detail per dokumen / nopen + lot
  const renderDocTable = (itemRecord, customerRecord) => {
    const columns = [
      {
        title: "Nopen",
        dataIndex: "nopen",
        key: "nopen",
        width: 110,
        fixed: "left",
        render: (text, record) => (
          <Button
            type="link"
            style={{
              color: "#1890ff",
              cursor: "pointer",
              fontWeight: "bold",
              padding: 0,
            }}
            onClick={() => onNopenClick(record)}
          >
            {text}
          </Button>
        ),
      },
      {
        title: "Tgl. Nopen",
        dataIndex: "tanggal_nopen",
        key: "tanggal_nopen",
        width: 95,
        render: (value) => (
          <Text style={{ fontSize: 12 }}>
            {value ? dayjs(value).format("DD/MM/YYYY") : "-"}
          </Text>
        ),
      },
      {
        title: "Seri",
        dataIndex: "seri",
        key: "seri",
        width: 55,
        align: "center",
      },
      {
        title: "Lokasi",
        dataIndex: "lokasi_lot",
        key: "lokasi_lot",
        width: 75,
        align: "center",
        render: (text) => <Tag color="geekblue">{text || "-"}</Tag>,
      },
      {
        title: "Masa Timbun",
        dataIndex: "masa_timbun",
        key: "masa_timbun",
        width: 110,
        render: (days) => {
          const status = getMasaTimbunStatus(days);
          return (
            <Tag color={status.color} style={{ fontWeight: 500 }}>
              {days} hari
            </Tag>
          );
        },
      },
      {
        title: "Nilai Kemasan",
        dataIndex: "nilai_kemasan",
        key: "nilai_kemasan",
        width: 105,
        align: "right",
        render: (val, r) => (
          <Text strong style={{ color: "#722ed1" }}>
            {(val ?? r.total_ct)?.toLocaleString() || 0}
          </Text>
        ),
      },
      {
        title: "Nama Kemasan",
        dataIndex: "nama_kemasan",
        key: "nama_kemasan",
        width: 95,
        align: "center",
        render: (val, r) => (
          <Tag color="purple">{val || r.satuan_kemasan || "CT"}</Tag>
        ),
      },
      {
        title: "Nilai Subsatuan",
        dataIndex: "nilai_subsatuan",
        key: "nilai_subsatuan",
        width: 110,
        align: "right",
        render: (val, r) => (
          <Text strong style={{ color: "#fa8c16" }}>
            {Number((val ?? r.total_pce) || 0).toLocaleString()}
          </Text>
        ),
      },
      {
        title: "Nama Subsatuan",
        dataIndex: "nama_subsatuan",
        key: "nama_subsatuan",
        width: 105,
        align: "center",
        render: (val) => (
          <Tag color="cyan">{val || "BO"}</Tag>
        ),
      },
      {
        title: "Nilai Satuan",
        dataIndex: "nilai_satuan",
        key: "nilai_satuan",
        width: 105,
        align: "right",
        render: (val, r) => (
          <Text strong style={{ color: "#1890ff" }}>
            {Number((val ?? r.total_liter) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
        ),
      },
      {
        title: "Nama Satuan",
        dataIndex: "nama_satuan",
        key: "nama_satuan",
        width: 95,
        align: "center",
        render: (val) => (
          <Tag color="blue">{val || "LTR"}</Tag>
        ),
      },
    ];

    const dataSource = (itemRecord.docs || []).map((d) => ({
      ...d,
      id_customer: d.id_customer || customerRecord.id_customer,
      item_code: d.item_code || itemRecord.item_code,
    }));

    return (
      <div style={{ padding: "8px 0 8px 48px", background: "#fafafa" }}>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          rowKey={(row) =>
            `${row.id_customer}-${row.item_code}-${row.nopen}-${row.seri}-${row.lokasi_lot}`
          }
          size="small"
          scroll={{ x: 1000 }}
          expandable={{
            expandedRowRender: (docRow) => (
              <div style={{ padding: "8px 0 8px 24px" }}>
                <Text
                  strong
                  style={{
                    fontSize: 12,
                    color: "#666",
                    marginBottom: 8,
                    display: "block",
                  }}
                >
                  <HistoryOutlined /> Riwayat Transaksi
                </Text>
                <DocHistory doc={docRow} />
              </div>
            ),
            rowExpandable: () => true,
          }}
        />
      </div>
    );
  };

  // Level 2: table item per importir
  const renderItemTable = (customerRecord) => {
    const columns = [
      {
        title: "Barang (Kode & Nama)",
        dataIndex: "item_name",
        key: "item_name",
        render: (text, r) => {
          const kms = r.nama_kemasan || r.satuan_kemasan || "CT";
          let sub = r.nama_subsatuan || r.satuan_per_unit || "BO";
          if (sub === "LTR") {
            sub = /\b(CAN|CANS|CANNED|KALENG)\b/i.test(r.item_name || "") ? "CAN" : "BO";
          }
          const isi = Number(r.isi_kemasan) || 1;
          const unit = Number(r.isi_per_unit) || 0;
          let konvStr = `Konversi: 1 ${kms} = ${isi} ${sub}`;
          if (unit > 0) {
            const ltrPerUnit = unit / 1000;
            const ltrPerKms = Number((isi * ltrPerUnit).toFixed(3));
            konvStr = `Konversi: 1 ${kms} = ${isi} ${sub} @ ${unit} ML (${ltrPerKms} LTR)`;
          }

          return (
            <Space direction="vertical" size={1}>
              <Space>
                <Tag color="blue" style={{ fontWeight: "bold" }}>
                  {r.item_code}
                </Tag>
                <Text strong>{text}</Text>
              </Space>
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                {konvStr}
              </Text>
            </Space>
          );
        },
      },
      {
        title: "Nilai Kemasan",
        dataIndex: "nilai_kemasan",
        key: "nilai_kemasan",
        width: 115,
        align: "right",
        render: (val, r) => (
          <Text strong style={{ color: "#722ed1" }}>
            {(val ?? r.total_ct)?.toLocaleString() || 0}
          </Text>
        ),
      },
      {
        title: "Nama Kemasan",
        dataIndex: "nama_kemasan",
        key: "nama_kemasan",
        width: 100,
        align: "center",
        render: (val, r) => (
          <Tag color="purple">{val || r.satuan_kemasan || "CT"}</Tag>
        ),
      },
      {
        title: "Nilai Subsatuan",
        dataIndex: "nilai_subsatuan",
        key: "nilai_subsatuan",
        width: 115,
        align: "right",
        render: (val, r) => (
          <Text strong style={{ color: "#fa8c16" }}>
            {Number((val ?? r.total_pce) || 0).toLocaleString()}
          </Text>
        ),
      },
      {
        title: "Nama Subsatuan",
        dataIndex: "nama_subsatuan",
        key: "nama_subsatuan",
        width: 110,
        align: "center",
        render: (val) => (
          <Tag color="cyan">{val || "BO"}</Tag>
        ),
      },
      {
        title: "Nilai Satuan",
        dataIndex: "nilai_satuan",
        key: "nilai_satuan",
        width: 115,
        align: "right",
        render: (val, r) => (
          <Text strong style={{ color: "#1890ff" }}>
            {Number((val ?? r.total_liter) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
        ),
      },
      {
        title: "Nama Satuan",
        dataIndex: "nama_satuan",
        key: "nama_satuan",
        width: 100,
        align: "center",
        render: (val) => (
          <Tag color="blue">{val || "LTR"}</Tag>
        ),
      },
    ];

    return (
      <div style={{ padding: "8px 0 8px 24px", background: "#f5f5f5" }}>
        <Table
          columns={columns}
          dataSource={customerRecord.items || []}
          pagination={false}
          rowKey={(item) => item.item_code}
          size="small"
          scroll={{ x: 900 }}
          expandable={{
            expandedRowRender: (itemRecord) =>
              renderDocTable(itemRecord, customerRecord),
            rowExpandable: (item) =>
              Array.isArray(item.docs) && item.docs.length > 0,
          }}
        />
      </div>
    );
  };

  // Level 1: per importir
  const columns = [
    {
      title: "Nama Importir",
      dataIndex: "nama_customer",
      key: "nama_customer",
      render: (text, record) => (
        <Space>
          <ShopOutlined style={{ color: "#722ed1", fontSize: 16 }} />
          <Text strong style={{ fontSize: 14 }}>
            {text || record.id_customer || "-"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Total Kemasan",
      dataIndex: "nilai_kemasan",
      key: "nilai_kemasan",
      width: 160,
      align: "right",
      render: (val, r) => (
        <Space direction="vertical" size={0} style={{ textAlign: "right", width: "100%" }}>
          <Text strong style={{ fontSize: 14, color: "#722ed1" }}>
            {(val ?? r.total_ct)?.toLocaleString() || 0}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>Kemasan (CT/PK)</Text>
        </Space>
      ),
    },
    {
      title: "Total Subsatuan",
      dataIndex: "nilai_subsatuan",
      key: "nilai_subsatuan",
      width: 160,
      align: "right",
      render: (val, r) => (
        <Space direction="vertical" size={0} style={{ textAlign: "right", width: "100%" }}>
          <Text strong style={{ fontSize: 14, color: "#fa8c16" }}>
            {(val ?? r.total_pce)?.toLocaleString() || 0}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>Subsatuan (BO/PCE)</Text>
        </Space>
      ),
    },
    {
      title: "Total Satuan",
      dataIndex: "nilai_satuan",
      key: "nilai_satuan",
      width: 160,
      align: "right",
      render: (val, r) => (
        <Space direction="vertical" size={0} style={{ textAlign: "right", width: "100%" }}>
          <Text strong style={{ fontSize: 14, color: "#1890ff" }}>
            {Number((val ?? r.total_liter) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>Satuan (LTR)</Text>
        </Space>
      ),
    },
  ];

  return (
    <Card bodyStyle={{ padding: "20px" }} style={{ borderRadius: 10, marginBottom: 40 }}>
      {/* Header: Title, Description & Export Buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div style={{ maxWidth: 750 }}>
          <Title level={4} style={{ marginBottom: 4, fontWeight: 700, color: "#1f2937" }}>
            Monitoring Stock Berjalan
          </Title>
          <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
            Pantau stok barang berdasarkan importir, rincian dokumen pabean, dan masa timbun secara real-time.
          </Text>
        </div>
        <Space size={10} wrap>
          {canViewSummary && (
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={() =>
                handleExportExcel({
                  grandTotalPCE: grandTotalSubsatuan,
                  grandTotalCT,
                  type: "summary",
                  showZero,
                })
              }
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
                height: 38,
                borderRadius: 6,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Excel Summary
            </Button>
          )}
          {canViewDetail && (
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={() =>
                handleExportExcel({
                  grandTotalPCE: grandTotalSubsatuan,
                  grandTotalCT,
                  type: "detail",
                  showZero,
                })
              }
              style={{
                background: "#108ee9",
                borderColor: "#108ee9",
                fontWeight: 600,
                height: 38,
                borderRadius: 6,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Excel Detail
            </Button>
          )}
        </Space>
      </div>

      {/* Row Statistic Cards - 4 Kolom Penuh & Luas */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: "#f9f0ff", borderColor: "#d3adf7", borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>Total Importir</span>}
              value={totalCustomers}
              prefix={<ShopOutlined />}
              valueStyle={{ color: "#722ed1", fontSize: 20, fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: "#f9f0ff", borderColor: "#d3adf7", borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>Total Kemasan</span>}
              value={grandTotalCT}
              suffix={<span style={{ fontSize: 12, fontWeight: 600, marginLeft: 4 }}>CT</span>}
              valueStyle={{ color: "#722ed1", fontSize: 20, fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: "#e6f7ff", borderColor: "#91d5ff", borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>Total Satuan</span>}
              value={grandTotalSatuan}
              precision={2}
              suffix={<span style={{ fontSize: 12, fontWeight: 600, marginLeft: 4 }}>LTR</span>}
              prefix={<InboxOutlined />}
              valueStyle={{ color: "#1890ff", fontSize: 20, fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: "#fff7e6", borderColor: "#ffd591", borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>Total Subsatuan</span>}
              value={grandTotalSubsatuan}
              suffix={<span style={{ fontSize: 12, fontWeight: 600, marginLeft: 4 }}>BO</span>}
              valueStyle={{ color: "#fa8c16", fontSize: 20, fontWeight: 700 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filter Bar & Switch On/Off */}
      <Card
        size="small"
        style={{
          marginBottom: 20,
          background: "#fafafa",
          borderRadius: 8,
          border: "1px solid #e8e8e8",
        }}
        bodyStyle={{ padding: "14px 16px" }}
      >
        <Row gutter={[16, 14]} align="middle" justify="space-between">
          <Col xs={24} md={10} lg={9}>
            <Text strong style={{ fontSize: 12, display: "block", marginBottom: 6, color: "#4b5563" }}>
              Pencarian Cepat
            </Text>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              placeholder="Cari importir, nopen, kode / nama barang..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: "100%", height: 38, borderRadius: 6 }}
            />
          </Col>
          <Col xs={24} md={14} lg={15}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 12,
                flexWrap: "wrap",
                paddingTop: 18,
              }}
            >
              {/* TOMBOL SWITCH ON / OFF TAMPILKAN DATA STOK 0 */}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "6px 14px",
                  borderRadius: 8,
                  background: showZero ? "#fff7e6" : "#f6ffed",
                  border: `1px solid ${showZero ? "#ffd591" : "#b7eb8f"}`,
                }}
              >
                <Switch
                  checked={showZero}
                  onChange={handleToggleShowZero}
                  checkedChildren="Tampil"
                  unCheckedChildren="Sembunyi"
                  style={{
                    backgroundColor: showZero ? "#fa8c16" : "#52c41a",
                  }}
                />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <Text
                    strong
                    style={{
                      fontSize: 13,
                      color: showZero ? "#d48806" : "#2e7d32",
                      userSelect: "none",
                    }}
                  >
                    {showZero ? "Semua Barang Ditampilkan" : "Hanya Barang dengan Stok Aktif"}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {showZero ? "Termasuk data barang yang saldonya sudah 0" : "Barang yang saldonya 0 otomatis disembunyikan"}
                  </Text>
                </div>
              </div>

              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadData(showZero)}
                loading={loading}
                style={{ height: 38, borderRadius: 6, fontWeight: 500 }}
              >
                Segarkan
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Legend */}
      <div
        style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}
      >
        <Text type="secondary" style={{ marginRight: 4, fontSize: 12 }}>
          Masa Timbun:
        </Text>
        <Tag color="#52c41a">Fresh (&lt;180 hari)</Tag>
        <Tag color="#1890ff">Normal (180-365 hari)</Tag>
        <Tag color="#faad14">Warning (&gt;1 tahun)</Tag>
        <Tag color="#ff4d4f">Critical (&gt;3 tahun)</Tag>
      </div>

      <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 8 }}>
        💡 Klik tanda [+] pada baris importir untuk melihat rincian barang, dokumen pabean, dan masa timbun.
      </Text>

      {/* Main Table */}
      <Table
        loading={loading}
        columns={columns}
        dataSource={displayData}
        rowKey={(record) => record.id_customer}
        pagination={{
          pageSize: 20,
          showTotal: (total) => `Total ${total} importir`,
          showSizeChanger: true,
          responsive: true,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        scroll={{ x: 600 }}
        expandable={{
          expandedRowRender: renderItemTable,
          rowExpandable: (record) =>
            Array.isArray(record.items) && record.items.length > 0,
        }}
      />

      {/* Drawer for History */}
      <Drawer
        title={
          <Space>
            <HistoryOutlined />
            <span>Riwayat Transaksi - {selectedDoc?.nopen || ""}</span>
          </Space>
        }
        width={typeof window !== "undefined" && window.innerWidth < 768 ? "100%" : 850}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        destroyOnClose
      >
        {selectedDoc && <DocHistory doc={selectedDoc} />}
      </Drawer>
    </Card>
  );
}
